import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, PhoneCall, Network, Voicemail } from "lucide-react";
import { ThemeToggle } from "@/context/ThemeContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) nav("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-border">
        <div className="absolute inset-0 -z-10"
          style={{ background: "radial-gradient(600px 400px at 30% 20%, hsl(43 62% 52% / 0.12), transparent 60%)" }} />
        <Logo size={44} />
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight">
            Your entire phone system, <span className="gold-text">under one roof.</span>
          </h1>
          <p className="text-muted-foreground mt-4">
            Departments, unlimited extensions, SIP trunk routing and mobile push — the InContact
            Cloud PBX console you self-host on your own Azure VM.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            {[
              { icon: PhoneCall, t: "Unlimited Extensions" },
              { icon: Network, t: "SIP Trunk Routing" },
              { icon: Voicemail, t: "IVR · Queues · Voicemail" },
              { icon: ShieldCheck, t: "TLS + Push Ready" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-3 p-3 rounded-xl glass">
                <f.icon size={18} className="text-[hsl(var(--gold))]" />
                <span className="text-sm">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Eagle Commerce · InContact</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={40} /></div>
          <h2 className="font-display text-2xl font-bold">Sign in</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-7">Access your PBX management console.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                placeholder="you@company.com" autoComplete="username"
                className="mt-1.5 bg-card border-border h-11" data-testid="login-email" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Password</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                placeholder="••••••••" autoComplete="current-password" className="mt-1.5 bg-card border-border h-11" data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full h-11 bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
